import { Link } from "react-router-dom";
import { PAGE_HEROES } from "../content/page-copy";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import { PLATFORMS } from "../content/platforms";

const MCP_ENDPOINT = "https://mcp.comos-federation.com/mcp";

/** DocsPage — rebuilt on the design system. (CO 069) */
const HERO = PAGE_HEROES['docs']!;

export function DocsPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              One endpoint, every verb. Quick-start for agent builders, full
              reference per platform, and the concepts that make the OS the OS.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8">
              <Link to="/demo" className={buttonStyles({ variant: "primary" })}>
                Try it live →
              </Link>
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* Quick-start */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="Quick-start"
            title="Connect any agent in 5 minutes"
            lead="One endpoint. OAuth. SSE. Multi-tenant routing built in."
          />
          <motion.div
            {...inView}
            variants={fadeUp}
            className="mt-10 max-w-3xl mx-auto"
          >
            <Card className="bg-ink text-paper dark:bg-black font-mono text-sm overflow-x-auto">
              <p className="text-eyebrow uppercase text-signal-bright font-grotesk mb-3">
                MCP endpoint
              </p>
              <p className="text-paper/90 break-all mb-4">{MCP_ENDPOINT}</p>
              <p className="text-mute-soft">
                # 1. Authenticate (OAuth client credentials)
              </p>
              <p>
                <span className="text-flow-bright">POST</span>{" "}
                {MCP_ENDPOINT.replace("/mcp", "/oauth/token")}
              </p>
              <p className="mb-3">
                {"  grant_type="}
                <span className="text-flow-bright">"client_credentials"</span>
              </p>
              <p className="text-mute-soft">
                # 2. Discover what a tenant exposes
              </p>
              <p>
                <span className="text-signal-bright">
                  federation.list_tenants
                </span>
                ()
              </p>
              <p className="mb-3">
                <span className="text-signal-bright">
                  federation.tenant_info
                </span>
                ({"{ tenantId: "}
                <span className="text-flow-bright">"acme-audio"</span>
                {" }"})
              </p>
              <p className="text-mute-soft">
                # 3. Shop and buy — one atomic call
              </p>
              <p>
                <span className="text-signal-bright">catalog_search</span>(
                {"{ query: "}
                <span className="text-flow-bright">"wireless headphones"</span>
                {" }"})
              </p>
              <p>
                <span className="text-signal-bright">order_create</span>(
                {"{ items }"}){" "}
                <span className="text-mute-soft"># reserve → price → settle</span>
              </p>
            </Card>
          </motion.div>
        </Container>
      </Section>

      {/* Per-platform reference */}
      <Section>
        <Container>
          <SectionHead eyebrow="Reference" title="Every platform, every verb" />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3"
          >
            {PLATFORMS.map((p) => (
              <motion.div key={p.slug} variants={fadeUp}>
                <Link to={`/platforms/${p.slug}`} className="block h-full">
                  <Card hover className="h-full">
                    <h3 className="text-display-sm mb-1">{p.name}</h3>
                    <p className="text-sm text-mute dark:text-mute-soft mb-3">
                      {p.tagline}
                    </p>
                    {p.agentCall ? (
                      <code className="block rounded bg-mute-surface dark:bg-ink-line text-signal dark:text-signal-bright font-mono text-xs px-2 py-1.5 break-words">
                        {p.agentCall}
                      </code>
                    ) : (
                      <span className="inline-block rounded-pill border border-mute-line dark:border-ink-line px-2 py-0.5 text-[11px] uppercase tracking-wide text-mute dark:text-mute-soft">
                        Coming soon
                      </span>
                    )}
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Concepts */}
      <Section tint="sunk">
        <Container>
          <SectionHead eyebrow="Concepts" title="What makes the OS the OS" />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-5 md:grid-cols-2"
          >
            {[
              {
                k: "Tenants & federation",
                v: "Every operator is a tenant. One MCP endpoint federates across tenants — agents shop the network, not individual stores. The shape recurses: ComOS is a federation of federations, every world governing itself while one economy settles them all.",
                to: "/platforms",
              },
              {
                k: "Coms — the currency for agents",
                v: "One perishable, single-use unit, backed 1:1 by a dollar. Spent down per action, no tier ladder. The currency agents act in.",
                to: "/coms",
              },
              {
                k: "Agent protocols",
                v: "MCP for tools, UCP for catalog and orders, OpenAPI / REST for agents that call plain HTTPS. Checkout routes to the federation's atomic order_create.",
                to: "/agents",
              },
              {
                k: "Proof before done",
                v: "Every change to the OS passes a verification gate before it ships. The honesty rate is observable, live.",
                to: "/trust",
              },
              {
                k: "Run your own node",
                v: "The same runtime that serves our cloud installs on hardware you own — signed release train, outbound-only, your world on your silicon. Money truth stays at the hub: one economy, however deep the tree.",
                to: "/install",
              },
            ].map((c) => (
              <motion.div key={c.k} variants={fadeUp}>
                <Link to={c.to} className="block h-full">
                  <Card hover className="h-full">
                    <h3 className="text-display-sm mb-2">{c.k}</h3>
                    <p className="text-mute dark:text-mute-soft text-sm">
                      {c.v}
                    </p>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      <Section tint="ink">
        <Container className="text-center">
          <motion.div
            {...inView}
            variants={fadeUp}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-display-lg text-balance text-paper">
              Ready to build?
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/start"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                Get a tenant
              </Link>
              <Link
                to="/agents"
                className={buttonStyles({
                  variant: "outline",
                  size: "lg",
                  className:
                    "border-paper/25 text-paper hover:text-white hover:border-paper",
                })}
              >
                See the agent surface
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
